import { Component, OnInit, NgZone } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthservicesService } from '../../../../services/authservices.service';
import Swal from 'sweetalert2';

declare const google: any;

@Component({
  selector: 'app-create-job',
  templateUrl: './create-job.component.html',
  styleUrls: ['./create-job.component.css']
})
export class CreateJobComponent implements OnInit {

  form: FormGroup;
  submitted = false;
  isLoading = false;
  isEditMode = false;
  editingJobId: string | null = null;

  roleOptions = [
    'Executive / Senior-level Official', 'Mid-level Official', 'Manager / Supervisor',
    'Professional', 'Technician / Specialist', 'Creative', 'Sales', 'Administrative',
    'Assistant / Support Staff', 'Customer Service', 'Operations', 'Driver',
    'Craft Worker / Tradesman', 'Service Technician', 'Laborer', 'Hiring Manager', 'Other'
  ];

  industryOptions = [
    "Aerospace & Defense", "Agriculture", "Arts, Entertainment & Recreation", "Automotive",
    "Construction & Facility Services", "Education", "Energy, Mining & Utilities",
    "Financial Services", "Government & Public Administration", "Healthcare",
    "Hotels, Travel & Hospitality", "Human Resources & Staffing", "Information Technology",
    "Insurance", "Legal", "Management & Consulting", "Manufacturing",
    "Media & Communication", "Nonprofit & NGO", "Personal Consumer Services",
    "Pharmaceutical & Biotechnology", "Real Estate", "Restaurants & Food Services",
    "Retail & Wholesale", "Telecommunications", "Transportation & Logistics", "Other"
  ];

  remoteJobOptions = ['No', 'Yes', 'Mixed', 'Temporarily'];
  jobTypeOptions = ['Full-time', 'Part-time', 'Temporary / Gig', 'Contract', 'Internship', 'Commission'];
  applicationReceiveOptions = ['Application website', 'Email', 'Phone', 'In-person'];

  constructor(
    private fb: FormBuilder,
    private authService: AuthservicesService,
    private router: Router,
    private zone: NgZone,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      companyName: ['', Validators.required],
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      role: ['Other', Validators.required],
      phoneNumber: [''],
      companyValues: [''],
      companyWebsite: [''],
      hideName: [false],
      hideEmail: [false],
      hidePhone: [false],
      hideWebsite: [false],

      industry: ['Other', Validators.required],
      category: [''],
      jobTitle: ['', Validators.required],
      remoteJob: ['No'],
      jobLocation: this.fb.group({
        googleAddress: [''],
        streetAddress: [''],
        landMark: [''],
        city: [''],
        state: [''],
        postalCode: [''],
        country: [''],
        location: [null]
      }),
      showAddress: [false],
      showContactInfo: [false],

      jobType: ['Full-time'],
      jobPay: [''],
      jobDuties: [''],
      requiredSkills: [''],

      recruiting: this.fb.group({
        address: [''],
        city: [''],
        state: [''],
        postalCode: [''],
        country: [''],
        contactNumber: [''],
        limitToCurrentRegion: [false],
        showAddress: [false],
        showContact: [false]
      }),

      howReceiveApplications: ['Email', Validators.required],
      contactInformation: ['', Validators.required],
      requiredDocuments: [''],
      durationDays: [30]
    });

    const jobFromState = history.state?.job;
    if (jobFromState) {
      this.editJob(jobFromState);
    }
  }

  editJob(job: any) {
    this.isEditMode = true;
    this.editingJobId = job._id || job.id;

    this.form.patchValue({
      companyName: job.jobPost?.companyName || '',
      name: job.jobPost?.name || '',
      email: job.jobPost?.email || '',
      role: job.jobPost?.role || 'Other',
      phoneNumber: job.jobPost?.phoneNumber || '',
      companyValues: job.jobPost?.companyValues || '',
      companyWebsite: job.jobPost?.companyWebsite || '',
      hideName: job.jobPost?.NamePrivacy || false,
      hideEmail: job.jobPost?.EmailPrivacy || false,
      hidePhone: job.jobPost?.PhonePrivacy || false,
      hideWebsite: job.jobPost?.companyWebsitePrivacy || false,

      industry: job.jobBasics?.industry || 'Other',
      category: job.jobBasics?.category || '',
      jobTitle: job.jobBasics?.title || '',
      remoteJob: job.jobBasics?.remoteJob || 'No',
      showAddress: job.jobBasics?.isAddress || false,
      showContactInfo: job.jobBasics?.isContact || false,

      jobType: job.jobDetails?.jobType || 'Full-time',
      jobPay: job.jobDetails?.jobPay || '',
      jobDuties: job.jobDetails?.jobDuties || '',
      requiredSkills: job.jobDetails?.skills || '',

      howReceiveApplications: job.applicationSettings?.receiveType || 'Email',
      contactInformation: job.applicationSettings?.receiveValue || '',
      requiredDocuments: job.applicationSettings?.documents || '',
      durationDays: job.applicationSettings?.duration || 30
    });

    if (job.jobBasics?.location) {
      this.form.get('jobLocation')?.patchValue({
        googleAddress: job.jobBasics.location.googleAddress || '',
        streetAddress: job.jobBasics.location.streetAddress || '',
        landMark: job.jobBasics.location.landMark || '',
        city: job.jobBasics.location.city || '',
        state: job.jobBasics.location.state || '',
        postalCode: job.jobBasics.location.postalCode || '',
        country: job.jobBasics.location.country || '',
        location: job.jobBasics.location.location || null
      });
    }

    if (job.recruitingAddress) {
      this.form.get('recruiting')?.patchValue(job.recruitingAddress);
    }
  }

  get f() { return this.form.controls; }

  onJobLocationChange(place: google.maps.places.PlaceResult) {
    this.zone.run(() => {
      const addr: any = {
        googleAddress: place.formatted_address || place.name || '',
        streetAddress: place.formatted_address || '',
        location: null
      };

      const lat = place.geometry?.location?.lat();
      const lng = place.geometry?.location?.lng();
      if (lat && lng) addr.location = { type: 'Point', coordinates: [lng, lat] };

      for (const comp of place.address_components || []) {
        const t = comp.types[0];
        if (t === 'locality' || t === 'administrative_area_level_2') addr.city = comp.long_name;
        if (t === 'administrative_area_level_1') addr.state = comp.long_name;
        if (t === 'country') addr.country = comp.long_name;
        if (t === 'postal_code') addr.postalCode = comp.long_name;
      }

      this.form.patchValue({ jobLocation: addr });
    });
  }

  onSubmit() {
    this.submitted = true;

    if (this.form.invalid) {
      Swal.fire({
        icon: 'warning',
        title: 'Incomplete Form',
        text: 'Please fill all required fields correctly.',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    // Prepare payload first
    const val = this.form.value;

    const payload = {
      jobPost: {
        companyName: val.companyName?.trim(),
        name: val.name?.trim(),
        email: val.email?.trim().toLowerCase(),
        role: val.role,
        phoneNumber: val.phoneNumber?.trim() || null,
        companyValues: val.companyValues?.trim() || null,
        companyWebsite: val.companyWebsite?.trim() || null,
        companyWebsitePrivacy: val.hideWebsite,
        NamePrivacy: val.hideName,
        EmailPrivacy: val.hideEmail,
        PhonePrivacy: val.hidePhone
      },
      jobBasics: {
        industry: val.industry,
        category: val.category?.trim() || null,
        title: val.jobTitle?.trim(),
        location: {
          googleAddress: val.jobLocation.googleAddress?.trim() || null,
          streetAddress: val.jobLocation.streetAddress?.trim() || null,
          landMark: val.jobLocation.landMark?.trim() || null,
          city: val.jobLocation.city?.trim() || null,
          state: val.jobLocation.state?.trim() || null,
          postalCode: val.jobLocation.postalCode?.trim() || null,
          country: val.jobLocation.country?.trim() || null,
          location: val.jobLocation.location || null
        },
        remoteJob: val.remoteJob
      },
      jobDetails: {
        jobType: val.jobType,
        jobPay: val.jobPay?.trim() || null,
        jobDuties: val.jobDuties?.trim() || null,
        skills: val.requiredSkills?.trim() || null
      },
      recruitingAddress: {
        address: val.recruiting.address?.trim() || null,
        city: val.recruiting.city?.trim() || null,
        state: val.recruiting.state?.trim() || null,
        postalCode: val.recruiting.postalCode?.trim() || null,
        country: val.recruiting.country?.trim() || null,
        contactNumber: val.recruiting.contactNumber?.trim() || null,
        currentRegionStatus: val.recruiting.limitToCurrentRegion,
        addressStatus: val.recruiting.showAddress,
        contactStatus: val.recruiting.showContact
      },
      applicationSettings: {
        receiveType: val.howReceiveApplications,
        receiveValue: val.contactInformation?.trim(),
        documents: val.requiredDocuments?.trim() || null,
        duration: Number(val.durationDays) || 30
      }
    };

    this.isLoading = true;

    // ────────────────────────────────────────────────────────────────
    // Safeguard: Check if we have ID in edit mode
    // ────────────────────────────────────────────────────────────────
    if (this.isEditMode) {
      if (!this.editingJobId) {
        this.isLoading = false;
        Swal.fire({
          icon: 'error',
          title: 'Cannot Update',
          text: 'Job ID is missing. Please try again or contact support.',
        });
        console.error('Missing editingJobId in edit mode', this.editingJobId);
        return;
      }

      // UPDATE
      this.authService.updateJob(this.editingJobId, payload).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          Swal.fire({
            icon: 'success',
            title: 'Success!',
            text: response.message || 'Job updated successfully',
            timer: 2000,
            showConfirmButton: false
          }).then(() => {
            this.router.navigate(['/jobs']);
          });
        },
        error: (err) => {
          this.isLoading = false;
          Swal.fire({
            icon: 'error',
            title: 'Update Failed',
            text: err.error?.message || 'Failed to update job posting'
          });
        }
      });
    } else {
      // CREATE
      this.authService.createJob(payload).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          Swal.fire({
            icon: 'success',
            title: 'Success!',
            text: response.message || 'Job created successfully',
            timer: 2000,
            showConfirmButton: false
          }).then(() => {
            this.form.reset();
            this.router.navigate(['/jobs']);
          });
        },
        error: (err) => {
          this.isLoading = false;
          Swal.fire({
            icon: 'error',
            title: 'Creation Failed',
            text: err.error?.message || 'Failed to create job posting'
          });
        }
      });
    }
  }

  cancel() {
    this.form.reset();
    this.isEditMode = false;
    this.editingJobId = null;
    this.submitted = false;
    this.router.navigate(['/jobs']);
  }
}